import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import ModelTypeConstants, {
  FOLDER_ITEM_TYPES,
  FolderItemType,
  STORABLE_ITEM_TYPES,
  StorableItemType,
} from 'constants/ModelTypeConstants';
import {SequenceType} from 'constants/SequenceConstants';
import {sourceFromFile} from 'folder-items/FileUtil';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as InventoryFileUtil from 'inventory/InventoryFileUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {shouldShowWarning, StatusLabelComponent} from 'inventory/sample-status/SampleStatusLabel';
import {
  doesItemHaveBlockingStatusWarning,
  getSampleStatusTooltip,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {UNASSIGNED_TASK_TEXT} from 'processes/ProcessConstants';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import StorableDetailsDisplay from 'samples/StorableDetailsDisplay';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import OptionContent from 'ui-library/menus/OptionContent';
import {apiIdToIconName} from 'util/APIUtil';
import {capitalize} from 'util/NameUtil';
import {worklistTypeDisplayName} from 'worklists/WorklistConstants';
import {formatWorklistDate} from 'worklists/WorklistUtil';

import {AnalysisSearchAttributesGraphQL} from '../../../graphql-fragments/analysis-tool/__generated/AnalysisSearchAttributesGraphQL';
import {DatasetListingGraphQL} from '../../../graphql-fragments/data-frames/__generated/DatasetListingGraphQL';
import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {AnalyticsDashboardSearchAttributesGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsDashboardSearchAttributesGraphQL';
import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {ProcessOutputSummaryGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputSummaryGraphQL';
import {ProcessSummaryGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessSummaryGraphQL';
import {ProcessTaskSummaryGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskSummaryGraphQL';

export type SearchResultItemType =
  | ModelTypeConstants.ANALYSIS
  | ModelTypeConstants.ANALYTICS_DASHBOARD
  | ModelTypeConstants.BATCH
  | ConnectionListingGraphQL
  | DatasetListingGraphQL
  | ModelTypeConstants.ENTITY
  | FileListingGraphQL
  | FolderItemType
  | ModelTypeConstants.ORGANIZATION
  | ModelTypeConstants.REQUEST
  | ModelTypeConstants.STAGE_ENTRY
  | StorableItemType
  | ProcessSummaryGraphQL
  | ProcessTaskSummaryGraphQL
  | ProcessOutputSummaryGraphQL
  | ModelTypeConstants.TEAM
  | ModelTypeConstants.USER
  | ModelTypeConstants.WORKFLOW;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SearchResult = any;

function extraNamesForSearchResult(searchResult: SearchResult): Array<string> {
  if (!searchResult.item) {
    assertAndContinue(false, 'Expected item to be non-nullable for search results');
    return [];
  }
  const names: Array<string> = [];
  if (searchResult.item.humanId) {
    names.push(searchResult.item.humanId);
  }
  if (searchResult.item.bioentityAliasInfos) {
    names.push(
      ...InventoryFileUtil.getAliasesToDisplay(searchResult.name, searchResult.item.bioentityAliasInfos)
    );
  }

  // When using GQL to fetch the barcode, a barcode node is returned with a value field. When using REST,
  // such as in FillPlateSearchInput.tsx, the barcode is returned as a string. When we consolidate search dropdowns,
  // we'll switch to using GQL only, like in the new SearchInput component, but this is a temporary fix.
  const barcode = searchResult.item.barcode?.value ?? searchResult.item.barcode;
  if (barcode != null && barcode !== searchResult.name) {
    names.push(barcode);
  }
  return names;
}

// This whole component will only eventually be used in the consolidated dropdown, and EntityTypes are
// converted to the more granular USER or ORGANIZATION.
function isParty(type: string): boolean {
  return isEntity(type) || type === ModelTypeConstants.TEAM || type === ModelTypeConstants.BENCHLING_APP;
}
function isEntity(type: string): boolean {
  return (
    type === ModelTypeConstants.ENTITY ||
    type === ModelTypeConstants.USER ||
    type === ModelTypeConstants.ORGANIZATION
  );
}

interface ItemOptionNameProps {
  searchResult: SearchResult;
  iconOverride?: JSX.Element | null;
}

export function ItemOptionName({searchResult, iconOverride}: ItemOptionNameProps): JSX.Element {
  const subtitle = (() => {
    const extraNames = extraNamesForSearchResult(searchResult);
    if (extraNames.length > 0) {
      return extraNames[0];
    }

    if (isEntity(searchResult.type)) {
      return searchResult.type;
    }
    if (searchResult.type === ModelTypeConstants.TEAM) {
      return searchResult.type;
    }
    if (searchResult.type === ModelTypeConstants.BENCHLING_APP) {
      return 'app';
    }

    return null;
  })();
  const isOptionArchived =
    isArchived(searchResult.item) ||
    (searchResult.item.archiveData && searchResult.item.archiveData.archived);
  const title = isOptionArchived
    ? `${searchResult.name} (${
        searchResult.item.archiveRecord?.purpose || searchResult.item.archiveData?.purpose
      })`
    : searchResult.name;
  const item = searchResult.item;
  const icon =
    iconOverride ??
    (STORABLE_ITEM_TYPES.includes(searchResult.type) ? (
      <StatusLabelComponent
        isWarning={shouldShowWarning(item, hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1), true)}
        isBlocking={doesItemHaveBlockingStatusWarning(item)}
        showCheckmark={item.checkOutStatusInfo?.isAssignedToCurrentUser}
        message={null}
      />
    ) : null);
  return <ItemOptionTitle title={title} subtitle={subtitle} shouldFadeTitle={isOptionArchived} icon={icon} />;
}

export interface ItemOptionTitleProps {
  title: string | JSX.Element;
  subtitle?: string | null;
  shouldFadeTitle?: boolean;
  icon?: JSX.Element | null;
}

export function ItemOptionTitle({title, subtitle, shouldFadeTitle, icon}: ItemOptionTitleProps): JSX.Element {
  return (
    <div className="itemOption-name u-lr-split">
      <div className={css(styles.titleWrapper)}>
        <span className={classNames('itemOption-title', {'faded-title': shouldFadeTitle})}>{title}</span>
        {icon && <span className={css(styles.icon)}>{icon}</span>}
      </div>
      {subtitle && (
        <span className={classNames('__itemOption-name-extra u-mini-spacing-left', css(styles.subtitle))}>
          {subtitle}
        </span>
      )}
    </div>
  );
}

interface DetailsProps {
  searchResult: SearchResult;
}

function joinStringsWithDot(...tokens: Array<string>): string {
  return tokens.join(' · ');
}

export function AnalysisDetails({searchResult}: DetailsProps): JSX.Element {
  const analysis = searchResult.item as AnalysisSearchAttributesGraphQL;
  return <>{joinStringsWithDot(analysis.project.name, `Created by ${getCreator(searchResult)}`)}</>;
}

export function AnalyticsDashboardDetails({searchResult}: DetailsProps): JSX.Element {
  const dashboard = searchResult.item as AnalyticsDashboardSearchAttributesGraphQL;
  return <>{joinStringsWithDot(dashboard.project.name, `Created by ${getCreator(searchResult)}`)}</>;
}

export function BatchDetails({searchResult}: DetailsProps): JSX.Element {
  const fileName = searchResult.item.file ? searchResult.item.file.name : searchResult.item.filename;
  const creatorHandle = searchResult.item.creator ? searchResult.item.creator.handle : searchResult.creator;
  return (
    <>
      Batch of {fileName} {'\u00b7'} Created by {creatorHandle}
    </>
  );
}

export function EntityDetails({searchResult}: DetailsProps): JSX.Element {
  return <>@{searchResult.item.handle}</>;
}

export function TeamDetails({searchResult}: DetailsProps): JSX.Element {
  return <>@{searchResult.item.handle}</>;
}

export function BenchlingAppDetails({searchResult}: DetailsProps): JSX.Element {
  return <>@{searchResult.item.handle}</>;
}

export function FileDetails({searchResult}: DetailsProps): JSX.Element {
  const creator = getCreator(searchResult);
  const projectName = sourceFromFile(searchResult.item)!.name;
  return <>{creator != null ? `${projectName} \u00b7 Created by ${creator}` : `${projectName}`}</>;
}

function ConnectionMetadata({searchResult}: DetailsProps): JSX.Element {
  return <>{`Created by ${searchResult.item.creator.name}`}</>;
}

function DatasetMetadata({searchResult}: DetailsProps): JSX.Element {
  return <>{`Created by ${searchResult.creator || searchResult.item?.creator?.name}`}</>;
}

function FileMetadata({searchResult}: DetailsProps): JSX.Element {
  return <>{`Created by ${getCreator(searchResult)}`}</>;
}

function ProcessTaskMetadata({searchResult}: DetailsProps): JSX.Element | null {
  const executionType = searchResult.item.executionType;
  // Don't display unassigned text if the task cannot be assigned
  if (
    executionType &&
    !getProcessPlatformFeatureConfig(
      ProcessPlatformFeatureType.CAN_EDIT_ASSIGNEE,
      executionType,
      searchResult.item.schema.name
    )
  ) {
    return null;
  }
  if (searchResult.item.assignee) {
    return <>Assigned to {searchResult.item.assignee.name}</>;
  }
  return <>{UNASSIGNED_TASK_TEXT}</>;
}

function ProcessOutputMetadata({searchResult}: DetailsProps): JSX.Element {
  const creator = getCreator(searchResult);
  const taskName = searchResult.item.task.humanId;
  return <>{creator != null ? `${taskName} \u00b7 Created by ${creator}` : `${taskName}`}</>;
}

export function RequestDetails({searchResult}: DetailsProps): JSX.Element {
  return <>{`Created by ${getCreator(searchResult)}`}</>;
}

export function WorkflowDetails({searchResult}: DetailsProps): JSX.Element {
  return <>{searchResult.item.source.name}</>;
}

export function WorklistDetails({searchResult}: DetailsProps): JSX.Element {
  return (
    <>
      {capitalize(worklistTypeDisplayName(searchResult.item.itemType))} worklist, modified{' '}
      {formatWorklistDate(searchResult.item.modifiedAt, false)}
    </>
  );
}

export function StageEntryDetails({searchResult}: DetailsProps): JSX.Element {
  const creator = searchResult.creator;
  const projectName = searchResult.item.source.name;
  return <>{creator != null ? `${projectName} \u00b7 Created by ${creator}` : `${projectName}`}</>;
}

export const getCreator = (searchResult: SearchResult): string | undefined => {
  // Handles variations of creator fields in graphql results
  if (searchResult.creator) {
    if (searchResult.creator.name != null) {
      return searchResult.creator.name;
    } else {
      return searchResult.creator;
    }
  }
  const itemCreator = searchResult.item?.creator;
  if (itemCreator != null) {
    return itemCreator.name ?? itemCreator.handle;
  }

  return undefined;
};

export function DefaultMetadata({searchResult}: DetailsProps): JSX.Element | null {
  const creator = getCreator(searchResult);
  if (creator != null) {
    return <>Created by {creator}</>;
  } else {
    return null;
  }
}

/**
 * Get the appropriate icon for a search result.
 *
 * Follows the pattern of getIconForBioentity but for REST-style data instead of GraphQL.
 */
export function getIconIdForSearchResult(searchResult: SearchResult): BenchlingIconName {
  const schemaIconId = searchResult?.item?.tagSchema?.iconId;
  if (schemaIconId != null) {
    return schemaIconId;
  }

  if (searchResult.type === ModelTypeConstants.SEQUENCE) {
    if (searchResult.item.type === SequenceType.DOUBLE_STRANDED) {
      return FolderItemIcon.Sequence;
    } else if (searchResult.item.type === SequenceType.SINGLE_STRANDED) {
      return FolderItemIcon.RNASequence;
    } else {
      return FolderItemIcon.OligoV2;
    }
  } else if (searchResult.type != null) {
    return searchResult.type;
  }

  return apiIdToIconName(
    searchResult.item && searchResult.item.id != null ? searchResult.item.id : searchResult.item_id
  );
}

interface SearchResultOptionProps {
  searchResult: SearchResult;
  children?: ReactNode;
  iconOverride?: JSX.Element | null;
}

export function SearchResultOption({
  searchResult,
  children,
  iconOverride,
}: SearchResultOptionProps): JSX.Element {
  const metadata =
    children ??
    (() => {
      if (searchResult.type === ModelTypeConstants.BATCH) {
        return <BatchDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.BLOB) {
        return null; // We don't have any extra info to show for blobs
      } else if (isEntity(searchResult.type)) {
        return <EntityDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.CONNECTION) {
        return <ConnectionMetadata searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.DATASET) {
        return <DatasetMetadata searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.FILE) {
        return <FileMetadata searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.TEAM) {
        return <TeamDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.BENCHLING_APP) {
        return <BenchlingAppDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.PROCESS_TASK) {
        return <ProcessTaskMetadata searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.PROCESS_OUTPUT) {
        return <ProcessOutputMetadata searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.REQUEST) {
        return <RequestDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.WORKFLOW) {
        return <WorkflowDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.STAGE_ENTRY) {
        return <StageEntryDetails searchResult={searchResult} />;
      } else if (STORABLE_ITEM_TYPES.includes(searchResult.type)) {
        return <StorableDetailsDisplay searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.WORKLIST) {
        return <WorklistDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.ANALYSIS) {
        return <AnalysisDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.ANALYTICS_DASHBOARD) {
        return <AnalyticsDashboardDetails searchResult={searchResult} />;
      } else if (searchResult.type === ModelTypeConstants.WORKSHEET) {
        return null;
      } else if (FOLDER_ITEM_TYPES.includes(searchResult.type as ModelTypeConstants)) {
        return <FileDetails searchResult={searchResult} />;
      } else {
        return <DefaultMetadata searchResult={searchResult} />;
      }
    })();
  return (
    <ItemOption
      title={<ItemOptionName searchResult={searchResult} iconOverride={iconOverride} />}
      metadata={metadata}
    />
  );
}

interface ItemOptionProps {
  title: JSX.Element | string;
  metadata?: React.ReactNode;
}

export function ItemOption({title, metadata}: ItemOptionProps): JSX.Element {
  return (
    <div className={classNames(!metadata && 'u-tiny-spacing-top')}>
      {title}
      <div className="itemOption-metadata menu-option-metadata u-ellipsis-text">{metadata}</div>
    </div>
  );
}

export function getTooltipTitleForSearchResult(searchResult: SearchResult): string {
  let tooltipTitle;
  const extraNames = extraNamesForSearchResult(searchResult);
  if (extraNames.length > 0) {
    tooltipTitle = `${searchResult.name} (${extraNames.join(' ')})`;
  } else {
    tooltipTitle = `${searchResult.name}`;
  }
  return tooltipTitle;
}

export interface ItemOptionContentProps {
  optionData: OptionData;
  searchResultChildren?: ReactNode;
  tooltip?: {text: string; hideTooltip?: null | false} | {text?: null; hideTooltip: true} | null;
  iconOverride?: JSX.Element | null;
}

export default function ItemOptionContent(props: ItemOptionContentProps): JSX.Element {
  const searchResult = props.optionData.value;

  const getTooltip = (): string | null => {
    if (props.tooltip?.hideTooltip ?? false) {
      return null;
    }
    const sampleStatusTooltip = getSampleStatusTooltip(searchResult.item, {
      includeAvailability: true,
      includeAuthorization: hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1),
    });

    const tooltipTitleForSearchResult = props.tooltip?.text ?? getTooltipTitleForSearchResult(searchResult);
    if (sampleStatusTooltip) {
      return `${tooltipTitleForSearchResult}\n${sampleStatusTooltip}`;
    } else {
      return tooltipTitleForSearchResult;
    }
  };

  const avatar = isParty(searchResult.type) ? (
    <Avatar entity={searchResult.item} size={AvatarSize.SMALL} className="u-mini-spacing-right u-flex-none" />
  ) : null;

  return (
    <OptionContent
      icon={avatar ? undefined : getIconIdForSearchResult(searchResult)}
      avatar={avatar}
      tooltipTitle={getTooltip()}
      tooltipPlacement={FloatingPlacement.TOP}
    >
      <SearchResultOption searchResult={searchResult} iconOverride={props.iconOverride}>
        {props.searchResultChildren}
      </SearchResultOption>
    </OptionContent>
  );
}

const styles = StyleSheet.create({
  icon: {
    marginLeft: '12px',
  },
  titleWrapper: {
    flex: '0 1 auto',
    display: 'flex',
    overflow: 'hidden',
  },
  subtitle: {
    color: Color.GRAY_500,
    flex: '1 1 50%',
    overflow: 'hidden',
    textAlign: 'right',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
